
import React, { useMemo, useRef, useState } from "react";
import Engine from "json-rules-engine-simplified";
import { IChangeEvent, AjvError, FormProps, FormValidation } from "@rjsf/core";

import defaultExtraActions from "./extraActions";
import _debounce from "lodash/debounce";
import { IFormSchema } from "./models/IFormSchema";
import { uiSchemaToStaticErrors } from "./utils/uiSchemaToStaticErrors";
import { applyContext, removeContextData } from "./utils/context.helpers";
import { focusOnField } from "./utils/focusOnField";
import { transformErrorsToFinnish } from "./utils/transformErrorsToFinnish";
import { CUSTOM_FORMATS } from "./constants/CustomFormats";
import { FileUploadProvider, defaultOptions as mockUploadOptions } from "../sg-fileupload";
import { IWidgetOptions } from "./models/IWidgetOptions";
import { HprProvider, mockHprOptions } from "../sg-hpr";
import { SignatureWidgetProvider } from "./context/SignatureContext";
import { mockSignatureOptions } from "./utils/mockSignatureOptions";
import { FormDataProvider } from "./context/FormDataContext";
import { contextSchema as defaultContextSchema } from "./models/ISchemaContext";
import { FormContextMapper } from "./models/IFormContext";
import composeRules from "./utils/composeRules";
import ISchemaRule from "./models/ISchemaRule";
import { validateDateFields } from "./utils/validateDateFields";

const applyRules = require("react-jsonschema-form-conditionals").default;

export interface IFormWithRules extends Omit<FormProps<any>, "schema" | "uiSchema" | "rules"> {
    formSchema: IFormSchema;

    extraActions?: { [key: string]: any };
    contextData?: { [key: string]: any }
    contextSchema?: IFormSchema["schema"];
    focusOnError?: boolean;
    children?: any
}
type TWithRules = (Form: React.FC<any> | React.ComponentClass<any, any>, widgetOptions?: IWidgetOptions | undefined) => (props: IFormWithRules) => JSX.Element


export const withRules: TWithRules = (Form, widgetOptions) => {
    return (props: IFormWithRules) => {
        const {
            formSchema,

            formData: initialFormData,
            onSubmit,
            onChange,

            extraActions: moreExtraActions = {},
            contextSchema = defaultContextSchema,
            contextData = {},

            focusOnError = true,
            ...otherProps
        } = props;

        const { schema, uiSchema } = applyContext(contextSchema, formSchema.schema, formSchema.uiSchema);
        const extraActions = { ...defaultExtraActions, ...moreExtraActions };
        const formData = { ...initialFormData, context: contextData, };

        // Uses ref to obtain current formData in case component re-renders and tries to use initial data, configured above
        const formEL: any = useRef();
        const currentFormData = formEL.current?.formData || formData;


        const formContext = FormContextMapper.toStrictFormContext(otherProps.formContext, formSchema, currentFormData);

        const rules = (formSchema.rules || []) as Array<ISchemaRule>;
        const composedRules = composeRules(rules, formSchema, formContext);



        const FormWithConditionals = useMemo(() => {
            return applyRules(schema, uiSchema, composedRules, Engine, extraActions)(Form);
        }, [composedRules]); // eslint-disable-line



        const [submitted, setSubmitted] = useState(false);

        const transformFormData = (formData: any) => {
            formData = removeContextData(formData); // context is only meant for schema rules.
            return formData;
        };

        const handleSubmit = async (form: IChangeEvent<any> | any, e: any) => {


            const formData = transformFormData(form.formData);
            // pass event to parent
            if (onSubmit) {
                onSubmit({ ...form, formData }, e);
            }
            if (!submitted) {
                setSubmitted(true);
            }
        };

        const _handleChange = (form: IChangeEvent<any>) => {
            const formData = transformFormData(form.formData);

            // pass event to parent
            if (onChange) {
                onChange({ ...form, formData });
            };
        };


        const _handleError = (errors: { stack: string }[]) => {
            const firstError = errors[0];

            // try to move focus to first field giving element
            if (firstError && focusOnError) {
                focusOnField(firstError.stack.split(": ")[0]);
            }
            // pass event to parent
            if (props.onError) {
                props.onError(errors);
            }
        };


        const _handleErrorTransform = (errors: AjvError[]) => {
            if (!formEL.current?.state) {
                return errors;
            }
            let staticErros = uiSchemaToStaticErrors(formEL.current.state.uiSchema);

            // add static errors in uiSchema
            if (staticErros) {
                errors = errors.concat(staticErros);
            }

            // pass event to parent
            if (otherProps.transformErrors) {
                errors = otherProps.transformErrors(errors);
            };

            // translate errors
            errors = transformErrorsToFinnish(errors);

            //  console.info("[withRules.transformErrors]", { errors });
            return errors;
        };


        const _handleValidate = (formData: any, errors: FormValidation) => {
            console.log({ formData, errors })
            errors = validateDateFields(formSchema, formData, errors);
            if (otherProps.validate) {
                return otherProps.validate(formData, errors);
            };
            return errors
        };

        const debouncedChangeHandler = _debounce(_handleChange, 350);

        const context = otherProps.formContext




        return (
            <FileUploadProvider options={widgetOptions?.uploadOptions || mockUploadOptions}>
                <HprProvider options={widgetOptions?.hprOptions || mockHprOptions}>
                    <SignatureWidgetProvider options={widgetOptions?.signatureOptions || mockSignatureOptions}>
                        <FormDataProvider data={currentFormData}>
                            <FormWithConditionals
                                ref={formEL}
                                {...otherProps}
                                formContext={formContext}
                                onError={_handleError}
                                onSubmit={handleSubmit}
                                schema={schema}
                                uiSchema={uiSchema}
                                formData={currentFormData}
                                transformErrors={_handleErrorTransform}
                                validate={_handleValidate}
                                customFormats={CUSTOM_FORMATS}
                                onChange={debouncedChangeHandler}

                            />
                        </FormDataProvider>
                    </SignatureWidgetProvider>
                </HprProvider>
            </FileUploadProvider>

        );
    };
};

